import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import {
  MAX_post_image_SIZE,
  MAX_post_video_SIZE,
} from "../../shared/constant";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import EasyCropper from "react-easy-crop";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formData, setFormData] = useState({
    seller_id: "",
    media: null,
    start_date: "",
    end_date: "",
    is_enable: true,
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
  const [showMediaCrop, setShowMediaCrop] = useState(false);
  const [showVideoCrop, setShowVideoCrop] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState("");
  const [cropVideoSrc, setCropVideoSrc] = useState("");
  const cropperRef = useRef(null);
  
  // Video crop state
  const [videoCrop, setVideoCrop] = useState({ x: 0, y: 0 });
  const [videoZoom, setVideoZoom] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [videoCropArea, setVideoCropArea] = useState(null); // Reserved for future video processing

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.SOCIAL.BANNERS_LIST);
      if (response) {
        setBanners(response || []);
      } else {
        toast.error(response.message || "Failed to fetch banners");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  const fetchSellers = async () => {
    setSellersLoading(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.SELLERS.LIST);
      console.log(response)
      if (response) {
        setSellers(response || []);
      } else {
        toast.error(response.message || "Failed to fetch sellers");
      }
    } catch (error) {
      console.error("Error fetching sellers:", error);
      toast.error("Failed to fetch sellers");
    } finally {
      setSellersLoading(false);
    }
  };

  const handleMediaCrop = () => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const cropper = cropperRef.current.cropper;
      const dataURL = cropper.getCroppedCanvas().toDataURL();
      
      if (!dataURL) {
        toast.error("Failed to process image. Please try again.");
        return;
      }
      fetch(dataURL)
        .then(res => res.blob())
        .then(blob => {
          const croppedFile = new File(
            [blob],
            pendingMediaFile.name,
            {
              type: pendingMediaFile.type,
              lastModified: Date.now(),
            }
          );
          
          const maxSize = MAX_post_image_SIZE * 1024 * 1024;
          if (croppedFile.size > maxSize) {
            toast.error(
              `Media size should be less than ${MAX_post_image_SIZE}MB`
            );
            return;
          }

          const previewUrl = URL.createObjectURL(croppedFile);
          setFormData((prev) => ({
            ...prev,
            media: croppedFile,
          }));
          setImagePreview(previewUrl);
          setMediaType("image");
          setShowMediaCrop(false);
          setPendingMediaFile(null);
          setCropImageSrc("");
        })
        .catch(error => {
          console.error("Error processing cropped image:", error);
          toast.error("Failed to process cropped image. Please try again.");
        });
    }
  };

  const handleVideoCrop = () => {
    if (pendingVideoFile) {
      // For now, we'll use the original video file since react-easy-crop doesn't support video cropping
      // In a real implementation, you would need a video processing library
      const croppedFile = new File(
        [pendingVideoFile],
        pendingVideoFile.name,
        {
          type: pendingVideoFile.type,
          lastModified: Date.now(),
        }
      );
      
      const maxSize = MAX_post_video_SIZE * 1024 * 1024;
      if (croppedFile.size > maxSize) {
        toast.error(
          `Video size should be less than ${MAX_post_video_SIZE}MB`
        );
        return;
      }

      // Create proper video URL
      const videoUrl = URL.createObjectURL(croppedFile);
      
      setFormData((prev) => ({
        ...prev,
        media: croppedFile,
      }));
      setImagePreview(videoUrl);
      setMediaType("video");
      setShowVideoCrop(false);
      setPendingVideoFile(null);
      setCropVideoSrc("");
      setVideoCrop({ x: 0, y: 0 });
      setVideoZoom(1);
      setVideoCropArea(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      const file = files[0];
      if (file) {
        // Valid file extensions
        const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'mp4', 'mov', 'avi'];
        
        // Get file extension
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.split('.').pop();
        
        // Validate file extension
        if (!validExtensions.includes(fileExtension)) {
          toast.error(`Unsupported file format '${fileExtension}'. Allowed formats: ${validExtensions.join(', ').toUpperCase()}`);
          e.target.value = ''; // Clear the input
          setImagePreview(null);
          setMediaType(null);
          setFormData((prev) => ({
            ...prev,
            [name]: null,
          }));
          return;
        }
        
        // Check if file is image or video
        const fileType = file.type;
        const validImageTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        const validVideoTypes = [
          "video/mp4",
          "video/mov",
        ];
        
        if (validImageTypes.includes(fileType)) {
          // Check file size
          const maxSize = MAX_post_image_SIZE * 1024 * 1024;
          if (file.size > maxSize) {
            toast.error(
              `File size should be less than ${MAX_post_image_SIZE}MB`
            );
            e.target.value = "";
            return;
          }
          
          setPendingMediaFile(file);
          const reader = new FileReader();
          reader.onload = () => {
            setCropImageSrc(reader.result);
            setShowMediaCrop(true);
          };
          reader.readAsDataURL(file);
        } else if (validVideoTypes.includes(fileType)) {
          // Check file size
          const maxSize = MAX_post_video_SIZE * 1024 * 1024;
          if (file.size > maxSize) {
            toast.error(
              `File size should be less than ${MAX_post_video_SIZE}MB`
            );
            e.target.value = "";
            return;
          }
          
          setPendingVideoFile(file);
          // Create proper video URL for cropping
          const videoUrl = URL.createObjectURL(file);
          setCropVideoSrc(videoUrl);
          setShowVideoCrop(true);
        } else {
          toast.error("Only image or video files are allowed");
          e.target.value = ''; // Clear the input
          setImagePreview(null);
          setMediaType(null);
          setFormData((prev) => ({
            ...prev,
            [name]: null,
          }));
        }
      } else {
        setImagePreview(null);
        setMediaType(null);
      }
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSellerChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      seller_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const openAddModal = () => {
    setFormData({
      seller_id: "",
      media: null,
      start_date: "",
      end_date: "",
      is_enable: true,
      is_active: true,
    });
    setImagePreview(null);
    setMediaType(null);
    setShowMediaCrop(false);
    setShowVideoCrop(false);
    setPendingMediaFile(null);
    setPendingVideoFile(null);
    setCropImageSrc("");
    setCropVideoSrc("");
    setVideoCrop({ x: 0, y: 0 });
    setVideoZoom(1);
    setVideoCropArea(null);
    setShowModal(true);
  };

  const openDeleteModal = (banner) => {
    setDeletingBanner(banner);
    setDeleteModal(true);
  };

  const openViewModal = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedBanner(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.seller_id) {
      toast.error("Seller ID is required");
      return;
    }

    if (!formData.media) {
      toast.error("Banner media (image or video) is required");
      return;
    }

    if (!formData.start_date) {
      toast.error("Start date is required");
      return;
    }

    if (!formData.end_date) {
      toast.error("End date is required");
      return;
    }

    // Check if start date is today or in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(formData.start_date);
    
    if (startDate <= today) {
      toast.error("Start date must be tomorrow or later");
      return;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toast.error("End date must be after start date");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("seller", formData.seller_id);
      formDataToSend.append("media", formData.media);
      formDataToSend.append("start_date", formData.start_date);
      formDataToSend.append("end_date", formData.end_date);
      formDataToSend.append("is_enable", formData.is_enable);
      formDataToSend.append("is_active", formData.is_active);

      const response = await apiService.upload(
        API_ENDPOINTS.SOCIAL.BANNERS_CREATE,
        formDataToSend
      );

      if (response.data && response.data.id) {
        toast.success(response.message || "Banner created successfully");
        setShowModal(false);
        fetchBanners();
      } else {
        toast.error(response.message || "Failed to create banner");
      }
    } catch (error) {
      console.error("Error creating banner:", error);
      toast.error("Failed to create banner");
    }
  };

  const handleDelete = async () => {
    if (!deletingBanner) return;
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.SOCIAL.BANNERS_DELETE(deletingBanner.id)
      );
      if (response.success) {
        toast.success(response.message || "Banner deleted successfully");
        setDeleteModal(false);
        setDeletingBanner(null);
        fetchBanners();
      } else {
        toast.error(response.message || "Failed to delete banner");
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to delete banner");
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      seller_id: "",
      media: null,
      start_date: "",
      end_date: "",
      is_enable: true,
      is_active: true,
    });
    setImagePreview(null);
    setMediaType(null);
    setShowMediaCrop(false);
    setShowVideoCrop(false);
    setPendingMediaFile(null);
    setPendingVideoFile(null);
    setCropImageSrc("");
    setCropVideoSrc("");
    setVideoCrop({ x: 0, y: 0 });
    setVideoZoom(1);
    setVideoCropArea(null);
    
    // Clean up object URLs
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingBanner(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (isActive, isEnable) => {
    if (isActive && isEnable) {
      return <span className="badge bg-success">Active & Enabled</span>;
    } else if (isActive && !isEnable) {
      return <span className="badge bg-warning">Active but Disabled</span>;
    } else if (!isActive && isEnable) {
      return <span className="badge bg-info">Inactive but Enabled</span>;
    } else {
      return <span className="badge bg-secondary">Inactive & Disabled</span>;
    }
  };

  const isBannerExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const getSellerName = (sellerId) => {
    const seller = sellers.find(s => s.id === sellerId);
    return seller ? `${seller.firm_name} - ${seller.mobile_number}` : `Seller ID: ${sellerId}`;
  };

  const getMediaType = (banner) => {
    // Check if media_type is explicitly provided
    if (banner.media_type) {
      return banner.media_type;
    }
    
    // Get media URL (could be image_url or media_url)
    const mediaUrl = banner.media_url || banner.image_url;
    if (!mediaUrl) return null;
    
    // Detect based on file extension
    const url = mediaUrl.toLowerCase();
    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return 'image';
    } else if (url.match(/\.(mp4|mov|avi|webm|ogg)$/)) {
      return 'video';
    }
    
    // Default to image if can't determine
    return 'image';
  };

  const getMediaUrl = (banner) => {
    return banner.media_url || banner.image_url;
  };

  useEffect(() => {
    fetchBanners();
    fetchSellers();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      if (cropVideoSrc) {
        URL.revokeObjectURL(cropVideoSrc);
      }
    };
  }, [imagePreview, cropVideoSrc]);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Banner Management</h3>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="fe fe-plus"></i> Add New Banner
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Advertisement Banners</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Seller Name</th>
                        <th>Media</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                        <th>Expired</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {banners.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No banners found
                          </td>
                        </tr>
                      ) : (
                        banners.map((banner,index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                                {getSellerName(banner.seller)}
                            </td>
                            <td>
                              {getMediaUrl(banner) ? (
                                getMediaType(banner) === 'video' ? (
                                  <video
                                    src={getMediaUrl(banner)}
                                    style={{
                                      width: "60px",
                                      height: "40px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                    muted
                                    playsInline
                                  />
                                ) : (
                                  <img
                                    src={getMediaUrl(banner)}
                                    alt="Banner"
                                    style={{
                                      width: "60px",
                                      height: "40px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                  />
                                )
                              ) : (
                                <span className="text-muted">No Media</span>
                              )}
                            </td>
                            <td>{formatDate(banner.start_date)}</td>
                            <td>{formatDate(banner.end_date)}</td>
                            <td>{getStatusBadge(banner.is_active, banner.is_enable)}</td>
                            <td>
                              {isBannerExpired(banner.end_date) ? (
                                <span className="badge bg-danger">Expired</span>
                              ) : (
                                <span className="badge bg-success">Active</span>
                              )}
                            </td>
                            <td>
                              <div className="actions">
                                <button
                                  className="btn btn-sm btn-outline-info me-2"
                                  onClick={() => openViewModal(banner)}
                                  title="View Banner Details"
                                >
                                  <i className="fe fe-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openDeleteModal(banner)}
                                  title="Delete Banner"
                                >
                                  <i className="fe fe-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Banner Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Banner</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="seller_id" className="form-label">
                          Select Seller <span className="text-danger">*</span>
                        </label>
                        <Select
                          id="seller_id"
                          name="seller_id"
                          value={sellers.find(seller => seller.id === formData.seller_id) ? 
                            { value: formData.seller_id, label: `${sellers.find(seller => seller.id === formData.seller_id)?.firm_name} - ${sellers.find(seller => seller.id === formData.seller_id)?.mobile_number}` } : 
                            null
                          }
                          onChange={handleSellerChange}
                          options={sellers.map((seller) => ({
                            value: seller.id,
                            label: `${seller.firm_name} - ${seller.mobile_number}`
                          }))}
                          placeholder={sellersLoading ? "Loading sellers..." : "Select a seller"}
                          isDisabled={sellersLoading}
                          isClearable
                          isSearchable
                          className="react-select-container"
                          classNamePrefix="react-select"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px',
                              border: '1px solid #ced4da',
                              borderRadius: '0.375rem',
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: '#6c757d',
                            }),
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="media" className="form-label">
                          Banner Media (Image/Video) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="media"
                          name="media"
                          accept=".jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.avi,image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-msvideo"
                          onChange={handleInputChange}
                          required
                        />
                        <small className="form-text text-muted">
                          Supported formats: JPG, JPEG, PNG, WEBP, GIF, MP4, MOV, AVI - Max size: {MAX_post_image_SIZE}MB for images, {MAX_post_video_SIZE}MB for videos
                        </small>
                        {imagePreview && formData.media && (
                          <div className="mt-3">
                            {mediaType === "image" ? (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : mediaType === "video" ? (
                              <video
                                src={imagePreview}
                                controls
                                preload="metadata"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100px",
                                  borderRadius: "8px",
                                  objectFit: "cover"
                                }}
                              >
                                Your browser does not support the video tag.
                              </video>
                            ) : null}
                            <div className="mt-2">
                              <small className="text-success mb-2 d-block">
                                <i className="fas fa-check-circle me-1"></i>
                                Media ready for upload: {formData.media.name}
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  if (imagePreview) {
                                    URL.revokeObjectURL(imagePreview);
                                  }
                                  setFormData((prev) => ({
                                    ...prev,
                                    media: null,
                                  }));
                                  setImagePreview(null);
                                  setMediaType(null);
                                }}
                              >
                                <i className="feather icon-x"></i> Remove Media
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="start_date" className="form-label">
                          Start Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="start_date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleInputChange}
                          min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="end_date" className="form-label">
                          End Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="end_date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleInputChange}
                          min={formData.start_date ? new Date(new Date(formData.start_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="is_enable"
                            name="is_enable"
                            checked={formData.is_enable}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label" htmlFor="is_enable">
                            Enable Banner
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="is_active"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label" htmlFor="is_active">
                            Active Banner
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Banner
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete this banner for{" "}
                  <strong>{getSellerName(deletingBanner?.seller_id)}</strong>?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Banner Modal */}
      {showViewModal && selectedBanner && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Banner Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeViewModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Seller</label>
                      <p className="form-control-plaintext">{getSellerName(selectedBanner.seller)}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Start Date</label>
                      <p className="form-control-plaintext">{formatDate(selectedBanner.start_date)}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">End Date</label>
                      <p className="form-control-plaintext">{formatDate(selectedBanner.end_date)}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Status</label>
                      <div className="form-control-plaintext">
                        {getStatusBadge(selectedBanner.is_active, selectedBanner.is_enable)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Expiration Status</label>
                      <div className="form-control-plaintext">
                        {isBannerExpired(selectedBanner.end_date) ? (
                          <span className="badge bg-danger">Expired</span>
                        ) : (
                          <span className="badge bg-success">Active</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Banner Media</label>
                  <div className="text-center">
                    {getMediaUrl(selectedBanner) ? (
                      getMediaType(selectedBanner) === 'video' ? (
                        <video
                          src={getMediaUrl(selectedBanner)}
                          controls
                          className="img-fluid"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          src={getMediaUrl(selectedBanner)}
                          alt="Banner Preview"
                          className="img-fluid"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                        />
                      )
                    ) : (
                      <div className="text-muted py-4">
                        <i className="fe fe-image fs-1 d-block mb-2"></i>
                        No media available
                      </div>
                    )}
                  </div>
                </div>

               
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeViewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      <Modal
        size="lg"
        backdrop="static"
        keyboard={false}
        show={showMediaCrop}
        onHide={() => {
          setShowMediaCrop(false);
          setPendingMediaFile(null);
          setCropImageSrc("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Banner Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center">
            <Cropper
              aspectRatio={1}
              src={cropImageSrc}
              zoomable={false}
              minCropBoxHeight={50}
              minCropBoxWidth={50}
              background={false}
              responsive={true}
              cropBoxResizable={true}
              checkOrientation={false}
              guides={true}
              viewMode={1}
              ref={cropperRef}
              style={{ height: 400, width: "100%" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            className="me-2"
            onClick={() => {
              setShowMediaCrop(false);
              setPendingMediaFile(null);
              setCropImageSrc("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleMediaCrop}
          >
            Crop & Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Video Crop Modal */}
      <Modal
        size="lg"
        backdrop="static"
        keyboard={false}
        show={showVideoCrop}
        onHide={() => {
          setShowVideoCrop(false);
          setPendingVideoFile(null);
          setCropVideoSrc("");
          setVideoCrop({ x: 0, y: 0 });
          setVideoZoom(1);
          setVideoCropArea(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Banner Video</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="crop-container" style={{ position: 'relative', height: '400px', width: '100%', background: '#000' }}>
            <EasyCropper
              video={cropVideoSrc}
              crop={videoCrop}
              zoom={videoZoom}
              aspect={1}
              onCropChange={setVideoCrop}
              onZoomChange={setVideoZoom}
              onCropComplete={(croppedArea, croppedAreaPixels) => {
                setVideoCropArea(croppedAreaPixels);
                // videoCropArea is stored for potential future video processing
                console.log('Video crop area:', croppedAreaPixels);
              }}
              onVideoLoaded={(res) => {
                console.log('Video loaded:', res);
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            className="me-2"
            onClick={() => {
              setShowVideoCrop(false);
              setPendingVideoFile(null);
              setCropVideoSrc("");
              setVideoCrop({ x: 0, y: 0 });
              setVideoZoom(1);
              setVideoCropArea(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleVideoCrop}
          >
            Apply Crop
          </Button>
        </Modal.Footer>
      </Modal>

      {(showModal || deleteModal || showViewModal || showMediaCrop || showVideoCrop) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default BannerManagement;
